import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

function useCountUpOnVisible(target: number, duration: number, suffix: string = '', options: { threshold?: number } = {}) {
  const [value, setValue] = React.useState(0);
  const ref = React.useRef<HTMLSpanElement>(null);
  const [hasAnimated, setHasAnimated] = React.useState(false);

  React.useEffect(() => {
    if (!ref.current || hasAnimated) return;
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            let start = 0;
            const startTime = performance.now();
            const animate = (now: number) => {
              const elapsed = now - startTime;
              const progress = Math.min(elapsed / (duration * 1000), 1);
              const current = Math.floor(progress * (target - start) + start);
              setValue(current);
              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                setValue(target);
                setHasAnimated(true);
              }
            };
            requestAnimationFrame(animate);
            observer.disconnect();
          }
        });
      },
      { threshold: options.threshold ?? 0.6 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration, hasAnimated, options.threshold]);

  return [ref, value + suffix] as const;
}

const Fleet: React.FC = () => {
  const fleet = [
    {
      id: 1,
      name: 'Camión Plataforma',
      type: 'Carga Especializada',
      capacity: '30 toneladas',
      features: ['Carga voluminosa', 'Plataforma abierta', 'Equipos especiales', 'Maniobras complejas'],
      description: 'Plataforma abierta para transportar cargas voluminosas y equipos especiales.',
      image: '🚚'
    },
    {
      id: 2,
      name: 'Furgón Express',
      type: 'Carga Express',
      capacity: '30 toneladas',
      features: ['Entrega rápida', 'Prioridad máxima', 'Seguimiento GPS', 'Confirmación de entrega'],
      description: 'Vehículo ágil para envíos urgentes y entregas rápidas en áreas urbanas.',
      image: '🚐'
    }
  ];

  // Animaciones para stats
  const [vehiculosRef, vehiculosValue] = useCountUpOnVisible(5, 1, '');
  const [experienciaRef, experienciaValue] = useCountUpOnVisible(3, 1, '+');
  const [clientesRef, clientesValue] = useCountUpOnVisible(85, 1, '+');
  const [coberturaRef, coberturaValue] = useCountUpOnVisible(100, 1, '%');

  return (
    <>
      <Helmet>
        <title>Nuestra Flota | Royal Trucking</title>
        <meta name="description" content="Conoce la flota moderna y diversificada de Royal Trucking: camiones de carga seca, refrigerada, pesada, cisterna y más." />
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <section className="relative text-white" style={{ minHeight: '320px' }}>
          <img
            src="/fondoflota.webp"
            alt="Fondo Flota Royal Trucking"
            className="absolute inset-0 w-full h-full object-cover object-center z-0"
            style={{ filter: 'brightness(0.7)' }}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-primary-600/70 z-10"></div>
          <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center text-center py-20">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Nuestra Flota
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Contamos con una flota moderna y diversificada de vehículos especializados 
              para satisfacer todas sus necesidades de transporte de carga.
            </p>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2">
                  <span ref={vehiculosRef}>{vehiculosValue}</span>
                </div>
                <div className="text-gray-600 font-medium">Vehículos</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2">
                  <span ref={experienciaRef}>{experienciaValue}</span>
                </div>
                <div className="text-gray-600 font-medium">Años de Experiencia</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2">
                  <span ref={coberturaRef}>{coberturaValue}</span>
                </div>
                <div className="text-gray-600 font-medium">Cobertura Nacional</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2">
                  <span ref={clientesRef}>{clientesValue}</span>
                </div>
                <div className="text-gray-600 font-medium">Clientes Satisfechos</div>
              </div>
            </div>
          </div>
        </section>

        {/* Fleet Grid */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Tipos de Vehículos
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Nuestra flota incluye una amplia variedad de vehículos especializados 
                para diferentes tipos de carga y requerimientos específicos.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {fleet.map((vehicle) => (
                <div key={vehicle.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                  <div className="p-8">
                    <div className="text-center mb-6">
                      <div className="text-6xl mb-4">{vehicle.image}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {vehicle.name}
                      </h3>
                      <span className="inline-block bg-primary-100 text-primary-800 text-sm font-medium px-3 py-1 rounded-full">
                        {vehicle.type}
                      </span>
                    </div>
                    
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-medium text-gray-600">Capacidad:</span>
                        <span className="text-sm font-semibold text-gray-900">{vehicle.capacity}</span>
                      </div>
                      <p className="text-gray-600 text-sm mb-4">
                        {vehicle.description}
                      </p>
                    </div>

                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Características:</h4>
                      <ul className="space-y-2">
                        {vehicle.features.map((feature, index) => (
                          <li key={index} className="flex items-center text-sm text-gray-600">
                            <svg className="w-4 h-4 text-primary-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link
                      to="/contact"
                      className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors duration-200 text-center block"
                    >
                      Solicitar Cotización
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Fleet Features */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Características de Nuestra Flota
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Todos nuestros vehículos cuentan con tecnología avanzada y mantenimiento 
                preventivo para garantizar la máxima seguridad y eficiencia.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Mantenimiento Preventivo</h3>
                <p className="text-gray-600 text-sm">
                  Todos los vehículos reciben mantenimiento preventivo regular para garantizar su óptimo funcionamiento.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">GPS en Tiempo Real</h3>
                <p className="text-gray-600 text-sm">
                  Sistema de seguimiento GPS en todos los vehículos para monitoreo en tiempo real.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Seguridad Avanzada</h3>
                <p className="text-gray-600 text-sm">
                  Sistemas de seguridad modernos y conductores certificados para máxima protección.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Tecnología Moderna</h3>
                <p className="text-gray-600 text-sm">
                  Vehículos equipados con la última tecnología para eficiencia y confort.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              ¿Necesita un vehículo específico?
            </h2>
            <p className="text-xl mb-8 text-primary-100 max-w-2xl mx-auto">
              Nuestro equipo está listo para ayudarle a encontrar el vehículo perfecto 
              para sus necesidades de transporte específicas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              >
                Solicitar Vehículo
              </Link>
              <Link
                to="/services"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors duration-200"
              >
                Ver Servicios
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Fleet; 